// chart.js - The maths behind every price chart we draw, with no React in it.
//
// That is the point. The site renders these numbers into JSX and the standalone
// embed in client/embed renders the same numbers into an SVG string, so a line
// on somebody else's website is drawn by the same code as the line on ours. Two
// implementations of a chart would have drifted apart inside a month.
//
// Everything here takes plain numbers and returns plain numbers or path strings.
// Nothing measures the DOM, nothing holds state, and nothing formats money
// except through lib/format.js in the app, because a chart on a stranger's site
// has no access to that file. See `usd()` below.
//
// LEGEND
//   1. RANGES / METALS   the shared vocabulary, matched to the API
//   2. usd / compact     the two number formats a chart needs
//   3. timeLabels()      what goes under the x axis, per range
//   4. niceScale()       round grid values that contain the data
//   5. plot()            points plus a box, in, geometry out
//   6. crosshair()       which point is nearest a cursor

////////// 1. VOCABULARY //////////
// `key` matches the server. models/Price.js RANGES and lib/spot.js METALS are
// the other half of this, and the smoke test checks the metals agree.

export const RANGES = [
  { key: "1d", short: "1D", label: "24 hours" },
  { key: "1w", short: "1W", label: "7 days" },
  { key: "1m", short: "1M", label: "30 days" },
  { key: "6m", short: "6M", label: "6 months" },
  { key: "1y", short: "1Y", label: "1 year" },
  { key: "5y", short: "5Y", label: "5 years" },
];

// Muted on purpose. A chart that has to sit on an unknown website should read as
// a piece of print, not as a trading terminal, and four saturated lines would be
// the first thing somebody turned off.
export const METALS = [
  { key: "gold", label: "Gold", symbol: "XAU", color: "#8a6534" },
  { key: "silver", label: "Silver", symbol: "XAG", color: "#6f7377" },
  { key: "platinum", label: "Platinum", symbol: "XPT", color: "#4a6b7c" },
  { key: "palladium", label: "Palladium", symbol: "XPD", color: "#7c5a6b" },
];

export const metalOf = (key) => METALS.find((metal) => metal.key === key) || METALS[0];
export const rangeOf = (key) => RANGES.find((range) => range.key === key) || RANGES[0];

export const UP = "#3f6b4a";
export const DOWN = "#a33a2b";

////////// 2. NUMBERS //////////
// Deliberately not lib/format.js. The embed bundle cannot import from src/, and
// a chart only ever needs these two, so duplicating eleven lines is cheaper than
// making the app's formatting module load on a stranger's website.

export function usd(value, decimals) {
  if (!Number.isFinite(value)) return "—";
  const places = decimals ?? (Math.abs(value) < 10 ? 2 : Math.abs(value) < 1000 ? 2 : 0);
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: places, maximumFractionDigits: places })}`;
}

// Axis labels, where the difference between $4,482 and $4,483 is a pixel.
export function compact(value) {
  if (!Number.isFinite(value)) return "";
  if (Math.abs(value) >= 1000) return `$${Math.round(value).toLocaleString("en-US")}`;
  if (Math.abs(value) >= 10) return `$${value.toFixed(0)}`;
  return `$${value.toFixed(2)}`;
}

export const signed = (value, suffix = "%") =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(2)}${suffix}`;

////////// 3. TIME LABELS //////////
// A 24 hour chart wants clock times and a 5 year chart wants years. Getting this
// wrong is what makes a chart look like a component rather than a picture.

export function timeLabel(ms, range) {
  const date = new Date(ms);
  if (range === "1d") return date.toLocaleTimeString("en-US", { hour: "numeric" }).toLowerCase().replace(" ", "");
  if (range === "1w") return date.toLocaleDateString("en-US", { weekday: "short" });
  if (range === "1m" || range === "6m") return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (range === "1y") return date.toLocaleDateString("en-US", { month: "short" });
  return String(date.getFullYear());
}

////////// 4. SCALE //////////
// Grid lines land on numbers a person would have chosen. The data is padded by a
// tenth of its own span first, so a line never touches the top of its box, and a
// perfectly flat series still gets a sensible band around it rather than a
// division by zero.

export function niceScale(low, high, count = 4) {
  if (!Number.isFinite(low) || !Number.isFinite(high)) return { min: 0, max: 1, values: [] };

  if (high === low) {
    const pad = Math.abs(high) * 0.01 || 1;
    low -= pad;
    high += pad;
  } else {
    const pad = (high - low) * 0.12;
    low -= pad;
    high += pad;
  }

  const step = niceStep((high - low) / count);
  const min = Math.floor(low / step) * step;
  const max = Math.ceil(high / step) * step;

  const values = [];
  for (let value = min; value <= max + step / 2; value += step) {
    values.push(Number(value.toFixed(6)));
  }
  return { min, max, values };
}

// 1, 2, 2.5 or 5 times a power of ten, which covers everything from a silver
// chart moving in cents to a gold chart moving in hundreds.
function niceStep(rough) {
  if (!(rough > 0)) return 1;
  const power = 10 ** Math.floor(Math.log10(rough));
  const scaled = rough / power;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return step * power;
}

////////// 5. PLOT //////////
// `points` is [[ms, price]] exactly as the API returns it. `box` is the pixel
// size the caller measured. Everything comes back in pixels, ready to put
// straight into an SVG, because the alternative is a viewBox and a stroke that
// stretches into an oval when somebody makes their sidebar narrow.

export function plot(points = [], box = {}) {
  const width = Math.max(box.width || 0, 1);
  const height = Math.max(box.height || 0, 1);
  const pad = {
    top: box.padTop ?? 8,
    right: box.padRight ?? (box.axis === false ? 2 : 46),
    bottom: box.padBottom ?? (box.axis === false ? 2 : 22),
    left: box.padLeft ?? 2,
  };

  const plotWidth = Math.max(width - pad.left - pad.right, 1);
  const plotHeight = Math.max(height - pad.top - pad.bottom, 1);

  if (points.length < 2) {
    return { empty: true, width, height, pad, plotWidth, plotHeight, line: "", area: "", gridY: [], ticks: [], last: null, scale: { min: 0, max: 1, values: [] } };
  }

  const times = points.map((point) => point[0]);
  const values = points.map((point) => point[1]);
  const t0 = times[0];
  const t1 = times[times.length - 1];
  const span = Math.max(t1 - t0, 1);

  const scale = niceScale(Math.min(...values), Math.max(...values), box.gridCount ?? 4);
  const vSpan = Math.max(scale.max - scale.min, 1e-9);

  const x = (ms) => pad.left + ((ms - t0) / span) * plotWidth;
  const y = (value) => pad.top + (1 - (value - scale.min) / vSpan) * plotHeight;

  const line = points
    .map((point, index) => `${index ? "L" : "M"}${x(point[0]).toFixed(2)} ${y(point[1]).toFixed(2)}`)
    .join("");

  const floor = (pad.top + plotHeight).toFixed(2);
  const area = `${line}L${x(t1).toFixed(2)} ${floor}L${x(t0).toFixed(2)} ${floor}Z`;

  return {
    empty: false,
    width,
    height,
    pad,
    plotWidth,
    plotHeight,
    scale,
    x,
    y,
    line,
    area,
    first: values[0],
    last: { ms: t1, value: values[values.length - 1], x: x(t1), y: y(values[values.length - 1]) },
    gridY: scale.values.map((value) => ({ value, y: y(value), label: compact(value) })),
    ticks: axisTicks(points, x, plotWidth, box.range),
  };
}

// Roughly one label per 90 pixels, snapped to real points so a label never sits
// under a gap in the data. Fewer labels on a narrow embed, which is what stops
// the axis turning into a smear at 300px wide.
function axisTicks(points, x, plotWidth, range) {
  const wanted = Math.max(2, Math.min(6, Math.floor(plotWidth / 90)));
  const step = (points.length - 1) / (wanted - 1);

  const seen = new Set();
  const ticks = [];
  for (let index = 0; index < wanted; index += 1) {
    const point = points[Math.round(index * step)];
    if (!point) continue;
    const label = timeLabel(point[0], range);
    if (seen.has(label)) continue;
    seen.add(label);
    ticks.push({ ms: point[0], x: x(point[0]), label });
  }
  return ticks;
}

////////// 6. CROSSHAIR //////////
// Nearest point to a cursor, by x only. Nearest in two dimensions sounds better
// and feels worse: it jumps to a different time when the pointer moves straight
// up, which is exactly what somebody reading a value does not want.

export function crosshair(points, geometry, pixelX) {
  if (!points.length || geometry.empty) return null;

  let best = 0;
  let bestGap = Infinity;
  for (let index = 0; index < points.length; index += 1) {
    const gap = Math.abs(geometry.x(points[index][0]) - pixelX);
    if (gap < bestGap) { bestGap = gap; best = index; }
  }

  const point = points[best];
  return {
    index: best,
    ms: point[0],
    value: point[1],
    x: geometry.x(point[0]),
    y: geometry.y(point[1]),
  };
}

// The date under a crosshair, spelled out. Shorter than the axis label because
// this one has room and the axis does not.
export function crosshairLabel(ms, range) {
  const date = new Date(ms);
  const options = range === "1d" || range === "1w"
    ? { weekday: "short", hour: "numeric", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleString("en-US", options);
}
